import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from '../button/button';
import {RemoveButton} from '../button/remove-button/remove-button';
import type {ButtonVariant} from '../button/types';
import {Card} from '../card/card';
import {Link} from '../link/link';
import type {LinkVariant} from '../link/types';
import {MessageGroup} from '../message-group/message-group';

import styles from './cta-banner-with-buttons.module.css';

type LinkCTA = {
    text: string;
    href: string;
    onClick?: () => any;
    isRoutedLink?: boolean;
    newTab?: boolean;
    linkVariant?: LinkVariant;
};

type ButtonCTA = {
    text: string;
    onClick: () => any;
    buttonVariant?: ButtonVariant;
    getButtonRef?: any;
};

export type CTA = ButtonCTA | LinkCTA;

type Props = {
    image?: {
        src: string;
        width: number;
    };
    icon?: React.ComponentType<any>;
    title?: string | React.ReactNode;
    text: string;
    primaryCTA?: CTA;
    secondaryCTA?: CTA;
    onDismiss?: () => any;
    centerBannerContent?: boolean;
    includeImage?: boolean;
    includeBody?: boolean;
    includeRemoveButton?: boolean;
    fullSizeImage?: boolean;
};

export function CTABannerWithButtons(props: Props) {
    const {
        image,
        icon,
        title,
        text,
        primaryCTA,
        secondaryCTA,
        onDismiss,
        centerBannerContent,
        includeImage = true,
        includeBody = true,
        includeRemoveButton = true,
    } = props;

    function getCTA(cta?: CTA | null, isPrimaryCTA?: boolean) {
        if (!cta) return undefined;

        const defaultButtonVariant = isPrimaryCTA ? 'primary' : 'text-secondary';
        const defaultLinkVariant = isPrimaryCTA ? 'button-primary' : 'secondary';

        // @ts-expect-error
        if (cta.href) {
            // @ts-expect-error
            const linkProps = cta.isRoutedLink
                ? {
                      as: RoutedLink,
                      // @ts-expect-error
                      to: cta.href,
                  }
                : // @ts-expect-error
                  {href: cta.href};

            return (
                // @ts-expect-error
                <Link
                    size={isPrimaryCTA ? 'big' : 'normal'}
                    // @ts-expect-error
                    variant={cta.linkVariant ? cta.linkVariant : defaultLinkVariant}
                    onClick={cta.onClick}
                    // @ts-expect-error
                    newTab={cta.newTab}
                    {...linkProps}
                >
                    {cta.text}
                </Link>
            );
        } else {
            const button = (
                <Button
                    size={isPrimaryCTA ? 'big' : 'normal'}
                    // @ts-expect-error
                    getButtonRef={cta.getButtonRef ? cta.getButtonRef : undefined}
                    // @ts-expect-error
                    variant={cta.buttonVariant ? cta.buttonVariant : defaultButtonVariant}
                    onClick={cta.onClick}
                >
                    {cta.text}
                </Button>
            );

            return button;
        }
    }

    const illustration = image ? <img style={{width: image.width}} src={image.src} /> : undefined;

    return (
        <Card hasBorder={true} borderRadius={8}>
            <div className='flex full-width'>
                {props.fullSizeImage && includeImage && image ? (
                    // To display the image full size, we place it outside the centerBannerContent padding
                    <div className={styles['image-container']}>
                        <img src={image.src} className={styles['image']} />
                    </div>
                ) : undefined}
                <div
                    className={
                        centerBannerContent
                            ? styles['cta-banner-with-buttons--centered']
                            : styles['cta-banner-with-buttons']
                    }
                >
                    <MessageGroup
                        illustration={
                            includeImage && !props.fullSizeImage ? illustration : undefined
                        }
                        icon={icon}
                        title={title}
                        text={includeBody ? text : undefined}
                    />
                    <div className={styles['buttons-container']}>
                        <div className={styles['cta-buttons']}>
                            {getCTA(primaryCTA, true)}
                            {getCTA(secondaryCTA)}
                        </div>
                        {onDismiss && includeRemoveButton ? (
                            <RemoveButton onClick={onDismiss} />
                        ) : undefined}
                    </div>
                </div>
            </div>
        </Card>
    );
}
